import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonProps,
  Card,
  Input,
  InputNumber,
  InputNumberProps,
  InputProps,
  Row,
  RowProps,
  Spin,
  Tooltip,
} from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import theme from 'assets/styles/theme';
import { ButtonIcon, LoadingIcon } from 'components/common';
import { AudioEffectType, playAudio } from 'utils';

interface SmartScannerProps extends InputProps {
  id?: string;
  isAutoFocus?: number;
  onChangeValue: (value: string) => void;
  onTextChange?: (value: string) => void;
  setNull?: boolean;
  autoSelect?: boolean;
  disabled?: boolean;
  focusNum?: number;
  usualInput?: boolean;
  color?: string;
}
export const SmartScanner = (props: SmartScannerProps): JSX.Element => {
  const { onChangeValue, id, isAutoFocus, onTextChange, setNull = true, autoSelect, disabled, focusNum, usualInput, color, ...other } = props;
  const [value, setValue] = useState('');
  useEffect(() => {
    if (isAutoFocus && id) {
      setTimeout(() => {
        const elem = document.getElementById(id) as HTMLInputElement;
        if (elem) {
          elem.focus();
          if(autoSelect){
            elem.select()
          }
        }
      }, 500);
    }
  }, [id, isAutoFocus, autoSelect]);

  useEffect(() => {
    setValue(props.defaultValue ? `${props.defaultValue}` : '')
  }, [props.defaultValue])
  /**
   * handle  key down event
   */
  const handleKeyDown = useCallback(
    async (evt: any) => {
      if ([9, 13].indexOf(evt.keyCode) > -1) {
        evt.preventDefault();
        onChangeValue(value);
        if (autoSelect && id) {
          const elem = document.getElementById(id) as HTMLInputElement;
          if (elem) {
            elem.select();
          }
        }
        if (setNull) {
          setValue('');
        }
      }
    },
    [onChangeValue, value, setNull, autoSelect, id]
  );

  useEffect(()=>{
    if (focusNum && id) {
      setTimeout(() => {
        const elem = document.getElementById(id) as HTMLInputElement;
        if (elem) {
          elem.focus();
          // if(autoSelect){
          //   elem.select()
          // }
        }
      }, 300);
    }
  },[focusNum, id, autoSelect])
  return (
    usualInput ?
    <SInput
    $color={color}
    id={id}
    autoFocus={isAutoFocus ? true : false}
    allowClear
    onKeyDown={handleKeyDown}
    value={value}
    disabled={disabled}
    onFocus={(e)=>{
      if(autoSelect){
        e.currentTarget.select()
      }
    }}
    onChange={(e) => {
      onTextChange && onTextChange(e.target.value);
      setValue(e.target.value);
    }}
    /* eslint-disable react/jsx-props-no-spreading */
    {...other}
  />:
    <SmartInput
      id={id}
      $color={color}
      autoFocus={isAutoFocus ? true : false}
      allowClear
      onKeyDown={handleKeyDown}
      value={value}
      disabled={disabled}
      onFocus={(e)=>{
        if(autoSelect){
          e.currentTarget.select()
        }
      }}
      onChange={(e) => {
        onTextChange && onTextChange(e.target.value);
        setValue(e.target.value);
      }}
      /* eslint-disable react/jsx-props-no-spreading */
      {...other}
    />
  );
};
SmartScanner.defaultProps = {
  id: 'scannerInput',
  isAutoFocus: true,
};
interface SmartRowProps extends RowProps { }
export const SmartRow = (props: SmartRowProps): JSX.Element => {
  const { children, style, ...other } = props;
  return (
    <Row
      /* eslint-disable react/jsx-props-no-spreading */
      {...other}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...style,
      }}
    >
      {children}
    </Row>
  );
};
interface SmartButtonProps extends ButtonProps {
  type?: 'primary' | 'default';
  color?: string;
  isComingSoon?: boolean;
  backgroundColor?: string;
}
export const SmartButton = (props: SmartButtonProps): JSX.Element => {
  const {
    children,
    style,
    type,
    color = 'white',
    backgroundColor,
    isComingSoon,
    ...other
  } = props;
  const buttonStyle: StringKAnyVPair = {
    width: 250,
    marginBottom: 30,
    minHeight: 45,
    ...props.style
  };
  if (color && backgroundColor) {
    buttonStyle['color'] = color;
    buttonStyle['borderColor'] = backgroundColor;
    buttonStyle['backgroundColor'] = backgroundColor;
  }
  return (
    <Row justify="center" style={style}>
      {isComingSoon ? <Tooltip placement="topRight" title="coming soon">
        <Button disabled {...other} type={type || 'primary'} style={buttonStyle}>
          {children}
        </Button>
      </Tooltip> :
        <Button {...other} type={type || 'primary'} style={buttonStyle}>
          {children}
        </Button>}
    </Row>
  );
};
SmartButton.defaultProps = {
  type: undefined,
  color: undefined,
  backgroundColor: undefined,
};
type SmartFormLabelProps = {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  addonAfter?: ReactNode;
};
export function SmartFormLabel(props: SmartFormLabelProps): JSX.Element {
  const { children, style, addonAfter } = props;
  return (
    <div
      className={props.className}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        fontWeight: 500,
        ...style,
      }}
    >
      {children}
      <span>:</span>
      {addonAfter}
    </div>
  );
}
SmartFormLabel.defaultProps = {
  children: undefined,
  style: undefined,
};
type SmartBackButtonProps = {
  children?: React.ReactNode;
  expand?: boolean;
  onClick?: () => void;
  onHeightChange?: Function;
  type?: 'default' | 'warning';
};
export const SmartBackButton = (props: SmartBackButtonProps): JSX.Element => {
  const { onClick, children, type = 'default' } = props;
  const [more, setMore] = useState(props.expand || false);
  const blockRef = React.useRef<any>(null);
  const textRef = React.useRef<any>(null);
  const { t } = useTranslation()
  return (
    <>
      <SmartText
        ref={textRef}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setMore(!more);
          (props.onHeightChange) && props.onHeightChange();

          if (textRef.current && (!more)) {
            setTimeout(() => {
              if (blockRef.current) {
                const rect = blockRef.current.getBoundingClientRect();

                //console.log('r', rect);
                (rect.top - window.innerHeight > 10) && window.scrollBy({top: rect.height, behavior: 'smooth'});
              }
            }, 0);
          }
        }}
      >
        {t("smart.common.moreAction")}
      </SmartText>
      {more && !children && (
        <SmartBlock ref={blockRef}>
          <SmartButton
            backgroundColor={
              type === 'default' ? theme['@blue-fg'] : 'rgb(255,223,138)'
            }
            color={type === 'default' ? 'white' : 'black'}
            onClick={onClick}
          >
            {t("smart.common.goBack")}
          </SmartButton>
        </SmartBlock>
      )}
      {more && children && <SmartBlock ref={blockRef}>{children}</SmartBlock>}
    </>
  );
};
SmartBackButton.defaultProps = {
  onClick: undefined,
  children: undefined,
  type: undefined,
};
type SmartSpinProps = {
  loading: boolean;
};
export const SmartSpin = (props: SmartSpinProps): JSX.Element => {
  const { loading } = props;
  if (!loading) {
    return <></>;
  }
  return (
    <Row
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        marginTop: 10,
        marginBottom: 10,
      }}
    >
      <Spin spinning indicator={<LoadingIcon />} />
    </Row>
  );
};
type PickAllProps = {
  style: React.CSSProperties;
  onClick: () => void;
};
export function SmartPickAll(props: PickAllProps): JSX.Element {
  const { onClick, style } = props;
  const [pickedAll, setPickedAll] = useState(false);
  if (pickedAll) {
    return (
      <Button
        style={style}
        type="primary"
        onClick={() => {
          setPickedAll(false);
          onClick();
        }}
      >
        Picked All
      </Button>
    );
  }
  return (
    <ButtonIcon
      style={style}
      className="bi-three-dots"
      onClick={() => {
        setPickedAll(true);
      }}
    />
  );
};

export const GreenColor = 'rgb(86,149,34)';
export const YellowColor = 'red';

export const SmartSpace = styled(Row)`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  & .addon-btn-after {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  & .ant-input-affix-wrapper-focused {
    background-color: rgba(186, 230, 253, .2);
    box-shadow: 0 0 0 3px rgba(51, 95, 226, 0.2);
    padding-left: 4px;
    padding-right: 4px;
  }

  & .ant-input:focus,
  & .ant-input-number-input:focus {
    background-color: rgba(186, 230, 253, .1);
    padding-left: 6px;
    padding-right: 6px;
  }

  & .ant-input-number-input:focus {
    background-color: rgba(186, 230, 253, .3);
  }

  & .ant-input-group-wrapper.h-50 .ant-input-affix-wrapper {
    height: 50px;
  }

  & .ant-input-group-wrapper.h-50 .ant-input-group-addon {
    padding: 0;
  }

  & .ant-input-group-wrapper.h-50 .ant-btn {
    height: 48px;
    padding: 10px 12px;
    width: 48px;
  }

  & .b-b-bordered {
    border-bottom: 1px solid #F0F0F0;
  }

  & .b-r-bordered {
    border-right: 1px solid #F0F0F0;
  }

  & .b-t-bordered {
    border-top: 1px solid #F0F0F0;
  }

  & .b-r-10 {
    border-radius: 10px;
  }

  & .default-btn-danger {
    color: ${theme['@danger-color']};
    border-color: ${theme['@danger-color']};
  }

  & .default-btn-info {
    color: ${theme['@info-color']};
    border-color: ${theme['@info-color']};
  }

  & .default-btn-info[disabled] {
    color: rgba(0, 0, 0, .25);
    border-color: #D9D9D9;
  }

  & .card-body-p10 {
    padding: 10px;
  }

  & .card-body-p2 {
    padding: 2px;
  }

  & .fs-16,
  & .fs-16 .ant-input,
  & .fs-16 .anticon {
    font-size: 16px;
  }

  & .fs-18,
  & .fs-18 .ant-input,
  & .fs-18 .anticon {
    font-size: 18px;
  }

  & .fs-20,
  & .fs-20 .ant-input,
  & .fs-20 .anticon {
    font-size: 20px;
  }

  & .fs-22,
  & .fs-22 .ant-input,
  & .fs-22 .anticon {
    font-size: 22px;
  }

  & .fs-24,
  & .fs-24 .ant-input,
  & .fs-24 .anticon {
    font-size: 24px;
  }

  & .h-40 {
    height: 40px;
  }

  & .h-45 {
    height: 45px;
  }

  & .h-48 {
    height: 48px;
  }

  & .h-50 {
    height: 50px;
  }

  & .icon-btn-small {
    height: 28px;
    padding: 3px 6px;
  }

  & .li-row.bb-solid-1 {
    border-bottom: 1px solid #D9D9D9;
  }

  & .li-row.bb-solid-1:nth-child(even) {
    background-color: rgba(220, 220, 200, .2);
    //border-top: 1px solid #D9D9D9;
  }

  & .li-row.bb-solid-1:nth-child(odd) {
    background-color: rgba(220, 220, 220, .3);
    //border-top: 1px solid #D9D9D9;
  }

  & .max-w-250 {
    max-width: 250px;
  }

  &.mb-0,
  & .mb-0 {
    margin-bottom: 0 !important;
  }

  &.mb-5,
  & .mb-5 {
    margin-bottom: 5px;
  }

  &.mb-10,
  & .mb-10 {
    margin-bottom: 10px;
  }

  &.mb-30,
  & .mb-30 {
    margin-bottom: 30px;
  }

  & .ml-8 {
    margin-left: 8px;
  }

  & .mr-8 {
    margin-right: 8px;
  }

  & .mr-14 {
    margin-right: 14px;
  }

  & .mt-0 {
    margin-top: 0;
  }

  & .mt-10 {
    margin-top: 10px;
  }

  & .mt-15 {
    margin-top: 15px;
  }

  & .mt-2 {
    margin-top: 2px;
  }

  &.mt-20,
  & .mt-20 {
    margin-top: 20px;
  }

  &.mt-30,
  & .mt-30 {
    margin-top: 30px;
  }

  & .mt-5 {
    margin-top: 5px;
  }

  & .mt-8 {
    margin-top: 8px;
  }

  & .num-input {
    display: inline-block;
    font-size: 22px;
  }

  & .num-input .ant-input-number-group-addon {
    padding: 0;
  }

  & .num-input.h-40 .ant-btn {
    height: 38px;
    padding: 3px 8px;
    width: 38px;
  }

  & .num-input.h-40 .ant-input-number-input {
    height: 40px;
    padding: 0 6px;
  }

  & .num-input.h-50 .ant-btn {
    height: 48px;
    padding: 4px 12px;
    width: 48px;
  }

  & .num-input.h-50 .ant-input-number-input {
    height: 50px;
    padding: 0 6px;
  }

  & .num-input.nw-60 .ant-input-number {
    width: 60px;
  }

  & .num-input.nw-80 .ant-input-number {
    width: 80px;
  }

  & .p-12 {
    padding: 12px;
  }

  & .p-4 {
    padding: 4px;
  }

  & .p-4-2-2 {
    padding: 4px 2px 2px;
  }

  & .p-4-8 {
    padding: 4px 8px;
  }

  & .p-4-12 {
    padding: 4px 12px;
  }

  & .p-4-13 {
    padding: 4px 13px;
  }

  & .p-8-12 {
    padding: 8px 12px;
  }

  & .po-body-failed {
    background-color: ${theme['@danger-bg']};
    border-radius: 10px;
    color: #FFE5E5;
  }

  & .po-body-success {
    background-color: ${theme['@success-bg']};
    border-radius: 10px;
    color: #F3FBEF;
  }

  & .text-bold1 {
    font-weight: 550;
  }

  & .text-bold2 {
    font-weight: 600;
  }

  & .text-error {
    color: #CF1322;
  }

  & .text-error1 {
    color: #FF4D4F;
  }

  & .text-error2 {
    color: #FF7875;
  }

  & .text-green {
    color: green;
  }

  & .text-green-label {
    color: ${GreenColor};
  }

  & .text-grey {
    color: #666;
  }

  & .text-hover-blue:hover {
    color: ${theme['@blue-fg']};
  }

  & .text-info {
    color: ${theme['@info-color']};
  }

  & .text-primary {
    color: ${theme['@primary-color']};
  }

  & .tr-bg-green-01 {
    background-color: rgba(230, 244, 234, 1);
  }

  & .tr-bg-grey-01 {
    background-color: rgba(244, 244, 244, 0.9);
  }

  & .tr-odd-even:nth-child(even) .r-i-cell:nth-child(2),
  & .tr-odd-even:nth-child(even) .r-i-cell:nth-child(3),
  & .tr-odd-even:nth-child(even) .r-i-cell:nth-child(6),
  & .tr-odd-even:nth-child(even) .r-i-cell:nth-child(7) {
    background-color: rgba(244, 244, 244, 0.7);
  }

  & .tr-odd-even:nth-child(even) .r-i-cell:nth-child(2n+1) {
    border-right: 1px solid #E0E0E0;
  }

  & .tr-odd-even:nth-child(odd) .r-i-cell:nth-child(2),
  & .tr-odd-even:nth-child(odd) .r-i-cell:nth-child(3),
  & .tr-odd-even:nth-child(odd) .r-i-cell:nth-child(6),
  & .tr-odd-even:nth-child(odd) .r-i-cell:nth-child(7) {
    background-color: #FFF;
  }

  & .tr-odd-even:nth-child(odd) .r-i-cell:nth-child(2n+1) {
    border-right: 1px solid #D9D9D9;
  }

  & .tr-odd-even:nth-child(even) .b-b-bordered,
  & .tr-odd-even:nth-child(even) .b-r-bordered,
  & .tr-odd-even:nth-child(even) .b-t-bordered,
  & .tr-odd-even:nth-child(even) .ant-card-bordered {
    border-color: #E0E0E0;
  }

  & .tr-odd-even:nth-child(odd) .b-b-bordered,
  & .tr-odd-even:nth-child(odd) .b-r-bordered,
  & .tr-odd-even:nth-child(odd) .b-t-bordered,
  & .tr-odd-even:nth-child(odd) .ant-card-bordered {
    background-color: rgba(244, 244, 244, 0.4);
    border-color: #D9D9D9;
  }

  & .tr-bg-red-01 {
    background-color: #FEE2E2;
  }

  & .w-250 {
    width: 250px;
  }

  & .w-48 {
    width: 48px;
  }

  & .w-80 {
    width: 80px;
  }

  & .w-90 {
    width: 90px;
  }

  & .w-p100 {
    width: 100%;
  }

  &.y-scrollable,
  & .y-scrollable {
    overflow-y: auto;
  }
`;

export const SmartBlock = styled.div`
  margin-top: 20px;
`;
export const SmartText = styled.span`
  font-size: 20px;
  font-weight: 600;
  /* margin-left: 5px; */
`;
export const SmartLabel = styled.span`
  /* width: 65px; */
  padding-right: 5px;
`;
export const SmartInput = styled(Input)<{ $color?: string; }>`
  height: 50px;

  & .anticon {
    font-size: 24px;
  }

  & .ant-input {
    font-size: 26px;
    color: ${props => props.$color || "rgba(0, 0, 0, 0.85)"};
  }
`;
export const SmartInputNumber = styled(InputNumber)`
  height: 50px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  & .ant-input-number-input {
    font-size: 26px;
    text-align: center;
  }
`;

type SmartNumberInputProps = InputNumberProps & {
  version?: number;
};

export const SmartNumberInput = (props: SmartNumberInputProps) => {
  const [currentVersion, setCurrentVersion] = useState(typeof props.version === 'number' ? props.version : 0);
  const [value, setValue] = useState<any>(props.defaultValue || props.value);

  const addonAfter = () => {
    return (
      <Button
        className="fs-20 p-4-12"
        disabled={props.disabled}
        onClick={() => creaseValue(1)}
        type="text"
      >
        <PlusOutlined />
      </Button>
    );
  };

  const addonBefore = () => {
    return (
      <Button
        className="fs-20 p-4-12"
        disabled={props.disabled}
        onClick={() => creaseValue(-1)}
        type="text"
      >
        <MinusOutlined />
      </Button>
    );
  };

  const creaseValue = (increment: number) => {
    const val = typeof value === 'number' ? value : 0;

    onChange(val + increment);
  };

  const onChange = (val: any) => {
    if (typeof props.max === 'number' && val > props.max) return;
    if (typeof props.min === 'number' && val < props.min) return;
    // console.log('val', val, typeof val);
    setValue(val);

    if (props.onChange) props.onChange(val);
  };

  React.useEffect(() => {
    if (typeof props.version === 'number' && props.version !== currentVersion) {
      setValue(props.value);
      setCurrentVersion(props.version);
    }
  }, [currentVersion, props, value]);

  return (
    <InputNumber
      addonAfter={addonAfter()}
      addonBefore={addonBefore()}
      {...props}
      onChange={onChange}
      value={value}
    />
  );
};

interface SmartScannerInputProps extends InputProps {
  onClear?: Function;
  onScan?: Function;
  updateValue?: string;
  version?: number;
};

export const SmartScannerInput = (props: SmartScannerInputProps) => {
  const [attrs, setAttrs] = useState<StringKAnyVPair>({});
  const [defaultValue, setDefaultValue] = useState(props.defaultValue || props.updateValue);
  const [inited, setInited] = useState(false);
  const [lastVersion, setLastVersion] = useState(props.version);
  const [version, setVersion] = useState(0);

  const onChange = (evt: any) => {
    const t = evt.target.value;

    if (props.onChange) props.onChange(evt);

    if (props.onClear && t === '') props.onClear();
  };

  const onKeyDown = (evt: any) => {
    if (props.onKeyDown) props.onKeyDown(evt);

    if ([9, 13].indexOf(evt.keyCode) > -1) {
      if (props.onScan) props.onScan(evt.target.value, evt);
    }
  };

  useEffect(() => {
    if (!inited /*|| 'defaultValue' in props*/) {
      const ps = { ...props };

      delete ps.onClear;
      delete ps.onScan;
      delete ps.updateValue;
      setAttrs(ps);
      setInited(true);
    } else {
      if ('disabled' in props && props.disabled !== attrs.disabled) {
        setAttrs({
          ...attrs,
          addonAfter: props.addonAfter,
          disabled: props.disabled,
        });
      }
    }

    if ('updateValue' in props && defaultValue !== props.updateValue) {
      setDefaultValue(props.updateValue);
      setVersion(version + 1);
      //console.log('upd', props.updateValue);
    }

    if (lastVersion !== props.version) {
      for (const k in attrs) {
        if (attrs[k] !== (props as any)[k]) {
          attrs[k] = (props as any)[k];
        }
      }

      if ('defaultValue' in props && defaultValue !== props.defaultValue) setDefaultValue(props.defaultValue);

      setAttrs({...attrs});
      setLastVersion(props.version);
      setVersion(version + 1);
    }
    //console.log('->', lastVersion, defaultValue, props, attrs);
  }, [attrs, defaultValue, inited, lastVersion, props, version]);

  return (
    <Input
      {...attrs}
      defaultValue={defaultValue}
      key={`scanner-input${version}`}
      onChange={onChange}
      onKeyDown={onKeyDown}
    />
  );
};

export const SmartCard = styled(Card)`
  .ant-card-body {
    padding: 0px;
  }
  .ant-card-head {
    padding: 0px;
    min-height: 24px;
  }
  .ant-card-head-title {
    padding: 0px 0px;
  }
`

export const SInput = styled(Input)<{ $color?: string; }>`
  & .ant-input {
    color: ${props => props.$color || "rgba(0, 0, 0, 0.85)"};
  }
`;

// This function is the same with the util, but it is used on mobile
// It can used to determine whether use sound effect on mobile
// So please do not delete it
export const playSound = (sound: AudioEffectType) => {
  const useSoundEffect = true;

  if (useSoundEffect) playAudio(sound);
};
